import {FC, KeyboardEvent as ReactKeyboardEvent, ReactNode, useEffect, useId, useRef} from 'react'
import './Alert.css'

type props = {
  type?: 'success' | 'warning' | 'danger' | 'info' | 'primary'
  size?: 'lg' | 'xl'
  body?: ReactNode
  icon?: string
  title?: ReactNode
  /** No longer shown: the «?» to the old FAQ page was removed (2026-10-17). Kept so callers need no change. */
  help?: boolean
  cancel?: {
    text: string
    fn: () => void
  }
  ok?: {
    text: ReactNode
    fn: () => void
    type?:
      | 'btn-success'
      | 'btn-warning'
      | 'btn-danger'
      | 'btn-info'
      | 'btn-outline outline-success'
      | 'btn-outline outline-warning'
      | 'btn-outline outline-danger'
      | 'btn-outline outline-info'
  }
  otherBtn?: {
    text: ReactNode
    fn: () => void
    type:
      | 'btn-success'
      | 'btn-warning'
      | 'btn-danger'
      | 'btn-info'
      | 'btn-outline outline-success'
      | 'btn-outline outline-warning'
      | 'btn-outline outline-danger'
      | 'btn-outline outline-info'
  }[]
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * The panel's confirmation and message window (user request 2026-10-17: «این پاپ اپ ها رو … بهینه و مرتب کن برای هم
 * موبایل و تبلت هم دسکتاپ»): a white card with the tone on its icon and edge, centred on a wide screen and a sheet at
 * the bottom of a phone, its buttons in one row a thumb away. Escape presses the close button (DialogEscapeKey), the
 * keyboard stays inside while it is open, and focus returns to where it was.
 */
const Alert: FC<props> = ({type = 'danger', body, ok, cancel, icon, title, otherBtn, size = 'lg'}) => {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    // The page underneath stops scrolling and the phone's bottom bar steps aside; another dialog may own the class.
    const addedModalOpen = !document.body.classList.contains('modal-open')
    if (addedModalOpen) document.body.classList.add('modal-open')
    dialogRef.current?.focus({preventScroll: true})
    return () => {
      if (addedModalOpen && !document.querySelector('.modal.show')) {
        document.body.classList.remove('modal-open')
      }
      if (opener && document.contains(opener)) opener.focus({preventScroll: true})
    }
  }, [])

  // Tab and Shift+Tab go round the window's own buttons.
  const keepFocusInside = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return
    const controls = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []
    ).filter((control) => control !== dialogRef.current)
    if (controls.length === 0) {
      event.preventDefault()
      return
    }
    const first = controls[0]
    const last = controls[controls.length - 1]
    if (
      event.shiftKey &&
      (document.activeElement === first || document.activeElement === dialogRef.current)
    ) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const hasActions = Boolean(ok || cancel || otherBtn?.length)

  return (
    <>
      <div
        ref={dialogRef}
        className={`modal fade show d-block mp-alert mp-alert--${type}`}
        role='dialog'
        aria-modal='true'
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onKeyDown={keepFocusInside}
      >
        <div
          className={`modal-dialog modal-dialog-centered mp-alert__dialog mp-alert__dialog--${size}`}
        >
          <div className='modal-content mp-alert__content'>
            {cancel && (
              <button
                type='button'
                className='mp-alert__close'
                aria-label='بستن'
                data-dialog-close
                onClick={cancel.fn}
              >
                <i className='las la-times' aria-hidden='true'></i>
              </button>
            )}

            <div className='mp-alert__body'>
              {icon && (
                <span className='mp-alert__icon' aria-hidden='true'>
                  <i className={`las ${icon}`}></i>
                </span>
              )}
              {title && (
                <h2 id={titleId} className='mp-alert__title'>
                  {title}
                </h2>
              )}
              {body && <div className='mp-alert__text'>{body}</div>}
            </div>

            {hasActions && (
              <div className='mp-alert__actions'>
                {ok && (
                  <button
                    type='button'
                    className={`btn ${ok.type ? ok.type : 'btn-' + type}`}
                    onClick={ok.fn}
                  >
                    {ok.text}
                  </button>
                )}
                {otherBtn?.map((btn, ind) => (
                  <button
                    key={`btn_${ind}`}
                    type='button'
                    className={`btn ${btn.type}`}
                    onClick={btn.fn}
                  >
                    {btn.text}
                  </button>
                ))}
                {cancel && (
                  <button
                    type='button'
                    className='btn btn-light mp-alert__cancel'
                    onClick={cancel.fn}
                  >
                    {cancel.text}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show mp-alert-backdrop'></div>
    </>
  )
}

export {Alert}
